/**
 * Ключ идентичности пользователя — логин в нижнем регистре: в `login` лежит регистр последнего входа,
 * а в базе (владелец комнаты, модераторы) — тот, что был на момент записи. Сравнивать логины напрямую нельзя.
 */
export function isSameLogin(first: string | undefined | null, second: string | undefined | null): boolean {
    if (!first || !second) return false

    return first.toLowerCase() === second.toLowerCase()
}

export interface User {
    readonly login: string
    readonly sex: Sex
    readonly roles: Array<Roles>
    readonly willDelete?: number
    readonly premiumUntil?: number
    readonly boostInUsersList?: boolean
    /**
     * Относительный путь до файла аватара (preset или custom-загрузка), без базового URL.
     * Базовый URL клиент строит сам из текущего домена (images.{domain}/, см. ImageLoaderService) —
     * тем же способом, что и для картинок чата.
     */
    readonly avatarPath?: string
}

/**
 * Уровень приёма приватных сообщений.
 *  - `all` — принимать от всех (значение по умолчанию).
 *  - `favorites_only` — принимать только от избранных (премиум).
 *  - `disabled` — не принимать ни от кого (премиум).
 */
export enum AllowPrivateMessages {
    all = 'all',
    favorites_only = 'favorites_only',
    disabled = 'disabled'
}

/**
 * Тип, который используется на клиенте
 */
export interface ClientUser extends User {
    readonly registered: boolean
    readonly invisible: boolean
    readonly allowPrivateMessages: AllowPrivateMessages
}

export interface MeUser extends ClientUser {
    readonly money: number;
    readonly email?: string;
    /**
     * Id выбранного preset-аватара — нужен только "мне" (для подсветки текущего выбора
     * в диалоге "О себе"), другим пользователям виден только результирующий avatarPath.
     */
    readonly avatarPresetId?: string;
}

export function sexTranslateKey(sex: Sex): string {
    switch (sex) {
        case Sex.female:
            return 'sex.female'
        case Sex.male:
            return 'sex.male'
        case Sex.trans:
            return 'sex.trans'
    }
}

export function parseSex(sex: string): Sex | undefined {
    let enumSex: Sex
    if (Sex.male == sex) {
        enumSex = Sex.male
    } else if (Sex.female == sex) {
        enumSex = Sex.female
    } else if (Sex.trans == sex) {
        enumSex = Sex.trans
    } else {
        enumSex = undefined
    }

    return enumSex
}

export enum Sex {
    female = 'female',
    male = 'male',
    trans = 'trans'
}

/**
 * Сексуальная ориентация. Поле профиля, необязательное.
 *
 * Ориентация НЕ зависит от пола: пара точно так же может быть гетеро или би, как и один человек.
 * Когда появится Sex.couple, состав пары (М+Ж / М+М / Ж+Ж) станет ОТДЕЛЬНЫМ полем
 * coupleComposition, а не значением этого enum: иначе значение нельзя интерпретировать, не зная
 * пола, а смена пола ломала бы уже выбранное. См. docs/specs/user-profile.md §3.
 */
export enum Orientation {
    hetero = 'hetero',
    homo = 'homo',
    bi = 'bi',
    asexual = 'asexual'
}

export function orientationTranslateKey(orientation: Orientation): string {
    switch (orientation) {
        case Orientation.hetero:
            return 'orientation.hetero'
        case Orientation.homo:
            return 'orientation.homo'
        case Orientation.bi:
            return 'orientation.bi'
        case Orientation.asexual:
            return 'orientation.asexual'
    }
}

export function parseOrientation(orientation: string): Orientation | undefined {
    switch (orientation) {
        case Orientation.hetero:
            return Orientation.hetero
        case Orientation.homo:
            return Orientation.homo
        case Orientation.bi:
            return Orientation.bi
        case Orientation.asexual:
            return Orientation.asexual
        default:
            return undefined
    }
}

export const ALL_ORIENTATIONS = [Orientation.hetero, Orientation.homo, Orientation.bi, Orientation.asexual]

export const ORIENTATIONS_WITH_TRANSLATE_KEYS = ALL_ORIENTATIONS.map((orientation) => {
    return {orientation: orientation, translateKey: orientationTranslateKey(orientation)}
})

export const ALL_GENDERS = [Sex.male, Sex.female, Sex.trans]

export const GENDERS_WITH_TRANSLATE_KEYS = ALL_GENDERS.map((sex) => {
    return {sex: sex, translateKey: sexTranslateKey(sex)}
})

export enum Roles {
    //TODO: Расширить список ролей по аналогии с платными комнатами
    /**
     * Доступны все остальные права
     */
    ADMIN = 'admin',
    /**
     * Может блокировать пользователей
     */
    MODERATOR = 'moderator',
    /**
     * Может разблокировать пользователей
     */
    UNBLOCK_USERS = 'unblock_users',
    /**
     * Может изменять закрепленное сообщение
     */
    EDIT_PINNED_MESSAGE = 'edit_pinned_message',
    /**
     * Может удалять стоп-слова
     */
    DELETE_STOP_WORDS = 'delete_stop_words',
}

export class RolesUtils {
    static isModerator(roles: Array<Roles>): boolean {
        return roles.includes(Roles.MODERATOR)
    }

    static canBlockModerators(roles: Array<Roles>): boolean {
        return roles.includes(Roles.ADMIN)
    }

    static canBlockUsers(roles: Array<Roles>): boolean {
        return roles.includes(Roles.MODERATOR) || roles.includes(Roles.ADMIN)
    }

    static canUnblockUser(roles: Array<Roles>): boolean {
        return roles.includes(Roles.UNBLOCK_USERS) || roles.includes(Roles.ADMIN)
    }

    static canDeleteStopWords(roles: Array<Roles>): boolean {
        return roles.includes(Roles.DELETE_STOP_WORDS) || roles.includes(Roles.ADMIN)
    }

    static isAdministrator(roles: Array<Roles>): boolean {
        return roles.includes(Roles.ADMIN)
    }

    static isModeratorOrBetter(roles: Array<Roles>): boolean {
        return roles.includes(Roles.MODERATOR) || roles.includes(Roles.ADMIN)
    }

    static hasPermission(permission: Roles, permissions: Array<Roles>): boolean {
        return permissions.includes(permission) || permissions.includes(Roles.ADMIN)
    }
}

